import { Button, Card, Typography } from '../../ui';
import { AgentDot } from '../../../AgentDot';
import { useTranslation } from '../../../../lib/i18n';
import { useMaestroBrief } from '../../../../lib/use-maestro-brief';
import { useMyAgents } from '../../../../lib/my-agents';
import { BriefArrowIcon } from '../../MaestroBrief/BriefArrowIcon';
import { BriefListenButton } from '../../MaestroBrief/BriefListenButton';
import { BriefMark } from '../../MaestroBrief/BriefMark';
import styles from './MapBriefCard.module.css';

/**
 * The morning meeting ON the map — the node above the agents ring is a real card, not a pill: the meeting's
 * title and time, who attended, the opening line (one agent's actual words, quoted), and the two things the
 * reader does with a brief — read the full meeting, or listen to it. Same data and same actions as the
 * home's brief hero (`Home2Brief` / `MaestroBrief`, via `useMaestroBrief`), laid out for a map node.
 *
 * Rendered inside the canvas through a `<foreignObject>`, so it pans and zooms with the world; the world
 * units are the card's px, and the brief region is framed so it reads at roughly 1:1 on landing.
 */
export function MapBriefCard() {
  const { t } = useTranslation();
  const brief = useMaestroBrief();
  const agentsState = useMyAgents();
  const quoteAgent = brief?.quote && agentsState.status === 'ready' ? agentsState.team.agents.find((a) => a.id === brief.quote?.agentId) : undefined;
  const bigText = brief?.quote?.text ?? brief?.headline;

  return (
    <Card size="sm" className={styles.card} data-testid="map-brief">
      <div className={styles.head}>
        <span className={styles.titleRow}>
          <BriefMark size={18} />
          <Typography variant="h4" color="primary" component="span" noWrap className={styles.title}>
            {brief ? t('brief.home2.title', { time: brief.time }) : t('home.map.brief')}
          </Typography>
        </span>
        {brief && brief.agentCount > 0 && (
          <span className={styles.attendees}>
            <span className={styles.dots}>
              {brief.agentIds.slice(0, 5).map((id) => (
                <span key={id} className={styles.dot}>
                  <AgentDot id={id} size={18} />
                </span>
              ))}
            </span>
            <Typography variant="p3" color="secondary" component="span" noWrap>
              {t(brief.agentCount === 1 ? 'brief.home2.agentMet' : 'brief.home2.agentsMet', { count: brief.agentCount })}
            </Typography>
          </span>
        )}
      </div>

      {bigText ? (
        <div className={styles.quoteBlock}>
          <p className={styles.quote}>{brief?.quote ? `“${bigText}”` : bigText}</p>
          {/* Who said it, on its own line: inline after a clamped quote it sat on the clipped third line. */}
          {brief?.quote && quoteAgent && (
            <span className={styles.attribution}>
              <AgentDot id={brief.quote.agentId} size={16} />
              <Typography variant="p3" color="secondary" component="span" noWrap>
                {quoteAgent.title ? `${quoteAgent.name} · ${quoteAgent.title}` : quoteAgent.name}
              </Typography>
            </span>
          )}
        </div>
      ) : (
        <Typography variant="p2" color="secondary" className={styles.quiet}>
          {t('home.map.quiet')}
        </Typography>
      )}

      {brief && (
        <div className={styles.actions}>
          <Button size="small" onClick={brief.open} endIcon={<BriefArrowIcon />}>
            {t('brief.home2.readFullMeeting')}
          </Button>
          <BriefListenButton iconOnly size="medium" />
        </div>
      )}
    </Card>
  );
}
